import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { WarningOctagon, CheckCircle, Receipt, Phone, ArrowClockwise } from "@phosphor-icons/react";

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("abierto"); // abierto|resuelto|all
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/complaints", { params });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando reclamos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const resolve = async (id) => {
    setActing(id);
    try {
      await api.post(`/complaints/${id}/resolve`);
      toast.success("Reclamo marcado como resuelto");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setActing(null);
    }
  };

  const openCounts = items.filter(c => c.status === "abierto").length;

  return (
    <div className="space-y-6" data-testid="complaints-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Panel · Reclamos</div>
          <h1 className="font-display text-3xl font-medium tracking-tight flex items-center gap-3">
            <WarningOctagon size={26} weight="fill" className="text-[#F97316]" />
            Reclamos y quejas
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            La IA detecta reclamos en tiempo real y los registra aqui. Resuelvelos y notifica al cliente.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="border-[#27272A] bg-[#121214] text-[#FAFAFA] hover:bg-[#1F1F23]"
          data-testid="refresh-complaints"
        >
          <ArrowClockwise size={14} className="mr-2" /> Actualizar
        </Button>
      </header>

      <div className="flex gap-2">
        {[
          { k: "abierto", l: `Abiertos (${openCounts})` },
          { k: "resuelto", l: "Resueltos" },
          { k: "all", l: "Todos" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`complaint-filter-${f.k}`}
            className={`px-3 h-9 text-xs uppercase tracking-wider rounded-md border transition-colors ${
              filter === f.k
                ? "bg-[#F97316] border-[#F97316] text-white"
                : "bg-[#121214] border-[#27272A] text-[#A1A1AA] hover:text-[#FAFAFA]"
            }`}
          >{f.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#71717A] text-sm">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-10 text-center">
          <WarningOctagon size={32} className="text-[#3F3F46] mx-auto mb-3" />
          <div className="text-sm text-[#A1A1AA]">
            Sin reclamos {filter === "abierto" ? "abiertos" : filter === "resuelto" ? "resueltos" : ""}.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <article
              key={c.id}
              data-testid={`complaint-${c.id}`}
              className={`bg-[#121214] border rounded-lg p-5 transition-colors ${
                c.status === "resuelto" ? "border-[#27272A] opacity-70" : "border-[#F97316]/40"
              }`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium">{c.contact_name || c.contact_phone || "Cliente"}</span>
                    {c.contact_phone && (
                      <span className="text-xs text-[#71717A] flex items-center gap-1">
                        <Phone size={10} />{c.contact_phone}
                      </span>
                    )}
                    {c.order_number && (
                      <Badge className="bg-[#5E6AD2]/15 text-[#5E6AD2] border-[#5E6AD2]/30 text-[10px] gap-1">
                        <Receipt size={10} />#{c.order_number}
                      </Badge>
                    )}
                    {c.status === "resuelto" ? (
                      <Badge className="bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30 text-[10px] gap-1">
                        <CheckCircle size={10} weight="fill" />Resuelto
                      </Badge>
                    ) : (
                      <Badge className="bg-[#F97316]/15 text-[#F97316] border-[#F97316]/30 text-[10px]">
                        Abierto
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-[#D4D4D8] font-medium">{c.reason}</div>
                  {c.excerpt && (
                    <blockquote className="mt-2 pl-3 border-l-2 border-[#F97316]/60 text-xs text-[#A1A1AA] italic">
                      "{c.excerpt}"
                    </blockquote>
                  )}
                  <div className="text-[10px] text-[#71717A] mt-2">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                {c.status !== "resuelto" && (
                  <Button
                    size="sm"
                    onClick={() => resolve(c.id)}
                    disabled={acting === c.id}
                    data-testid={`resolve-${c.id}`}
                    className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  >
                    <CheckCircle size={14} className="mr-1.5" weight="fill" />
                    {acting === c.id ? "..." : "Resolver"}
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
